<template>
  <div class="main-wrapper">
    <layoutheader></layoutheader>
    <breadcrumb-doctor title="Reports" text="Doctor" text1="Reports" />
    <div class="content">
      <div class="container">
        <div class="row">
          <doctorsidebar></doctorsidebar>
          <div class="col-md-7 col-lg-8 col-xl-9 adm-scope rep">

            <!-- Filters: one row above everything -->
            <div class="rep-filters">
              <div class="adm-tabs" role="tablist" aria-label="Period">
                <button v-for="p in presets" :key="p.id" type="button" class="adm-tab" role="tab"
                        :class="{ 'is-active': preset === p.id }" :aria-selected="preset === p.id" @click="choosePreset(p.id)">
                  {{ p.label }}
                </button>
              </div>
              <div v-if="preset === 'custom'" class="rep-custom">
                <input v-model="customStart" type="date" class="adm-input" aria-label="From" :max="customEnd || undefined" />
                <span class="adm-muted">to</span>
                <input v-model="customEnd" type="date" class="adm-input" aria-label="To" :min="customStart || undefined" />
                <button type="button" class="adm-btn adm-btn--primary adm-btn--sm" :disabled="!customStart || !customEnd" @click="load">Apply</button>
              </div>
              <div class="adm-toolbar__spacer"></div>
              <select v-model="clinicid" class="adm-select rep-clinic" aria-label="Clinic" @change="load">
                <option value="">All clinics</option>
                <option v-for="c in clinics" :key="c.clinicid" :value="c.clinicid">{{ c.clinicname }}</option>
              </select>
              <div class="rep-export">
                <button type="button" class="adm-btn adm-btn--sm" :disabled="!report || exporting" @click="exportExcel">
                  <i class="fas fa-file-excel"></i> Excel
                </button>
                <button type="button" class="adm-btn adm-btn--sm" :disabled="!report || exporting" @click="exportPdf">
                  <i class="fas fa-file-pdf"></i> PDF
                </button>
              </div>
            </div>
            <p class="adm-muted rep-range">
              {{ rangeLabel }}<template v-if="report"> · compared with {{ prevLabel }}</template>
            </p>

            <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

            <!-- Headline numbers -->
            <div class="rep-kpis">
              <div v-for="k in kpis" :key="k.label" class="adm-card rep-kpi">
                <span class="rep-kpi__label">{{ k.label }}</span>
                <span class="rep-kpi__value">
                  <span v-if="loading" class="adm-skeleton rep-skel"></span>
                  <template v-else>{{ k.value }}</template>
                </span>
                <span v-if="!loading && k.delta" class="rep-kpi__delta" :class="k.delta.tone">
                  <i class="fas" :class="k.delta.up ? 'fa-arrow-up' : 'fa-arrow-down'"></i> {{ k.delta.text }}
                </span>
                <span v-else-if="!loading" class="rep-kpi__delta rep-flat">{{ k.note || "—" }}</span>
              </div>
            </div>

            <div v-if="report && report.summary.unmarked" class="adm-alert adm-alert--warning">
              <i class="fas fa-clipboard-check"></i>
              <div>
                <strong>{{ report.summary.unmarked }} past appointment{{ report.summary.unmarked === 1 ? " is" : "s are" }} still marked “Booked”.</strong>
                Mark them as visited or no-show in
                <router-link to="/appointments" class="rep-link">Appointments</router-link>
                so your visit and no-show numbers are accurate.
              </div>
            </div>

            <!-- Trend -->
            <section class="adm-card rep-card">
              <div class="adm-card__header">
                <div>
                  <h3 class="adm-card__title">Appointments over time</h3>
                  <p class="adm-card__hint">{{ report && report.bucket === "month" ? "Per month" : "Per day" }}, by outcome.</p>
                </div>
                <div class="adm-toolbar__spacer"></div>
                <div class="adm-tabs" role="tablist" aria-label="View">
                  <button type="button" class="adm-tab" :class="{ 'is-active': !showTable }" @click="showTable = false">Chart</button>
                  <button type="button" class="adm-tab" :class="{ 'is-active': showTable }" @click="showTable = true">Table</button>
                </div>
              </div>
              <div class="adm-card__body">
                <div class="rep-legend" aria-hidden="true">
                  <span v-for="s in OUTCOMES" :key="s.key"><i :style="{ background: s.color }"></i>{{ s.label }}</span>
                </div>
                <div v-if="loading" class="adm-skeleton rep-chart-skel"></div>
                <div v-else-if="!hasData" class="adm-empty"><i class="fas fa-chart-bar"></i><strong>No appointments in this period</strong>Try a longer period or another clinic.</div>
                <apexchart v-else-if="!showTable" type="bar" height="300" :options="trendOptions" :series="trendSeries" />
                <div v-else class="adm-table-wrap rep-table">
                  <table class="adm-table">
                    <thead>
                      <tr>
                        <th>{{ report.bucket === "month" ? "Month" : "Date" }}</th>
                        <th v-for="s in OUTCOMES" :key="s.key" class="rep-num">{{ s.label }}</th>
                        <th class="rep-num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in nonEmptySeries" :key="row.period">
                        <td>{{ periodLabel(row.period) }}</td>
                        <td v-for="s in OUTCOMES" :key="s.key" class="rep-num">{{ row[s.key] }}</td>
                        <td class="rep-num adm-strong">{{ row.total }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <!-- When patients come -->
            <div class="rep-grid">
              <section class="adm-card rep-card">
                <div class="adm-card__header"><div>
                  <h3 class="adm-card__title">Busiest days</h3>
                  <p class="adm-card__hint">Appointments by weekday (excluding cancelled).</p>
                </div></div>
                <div class="adm-card__body">
                  <div v-if="loading" class="adm-skeleton rep-chart-skel rep-chart-skel--sm"></div>
                  <div v-else-if="!hasData" class="adm-muted rep-none">No data</div>
                  <apexchart v-else type="bar" height="220" :options="weekdayOptions" :series="weekdaySeries" />
                </div>
              </section>
              <section class="adm-card rep-card">
                <div class="adm-card__header"><div>
                  <h3 class="adm-card__title">Busiest hours</h3>
                  <p class="adm-card__hint">Appointments by start time.</p>
                </div></div>
                <div class="adm-card__body">
                  <div v-if="loading" class="adm-skeleton rep-chart-skel rep-chart-skel--sm"></div>
                  <div v-else-if="!report || !report.hours.length" class="adm-muted rep-none">No data</div>
                  <apexchart v-else type="bar" height="220" :options="hourOptions" :series="hourSeries" />
                </div>
              </section>
            </div>

            <!-- Who comes and why -->
            <div class="rep-grid">
              <section class="adm-card rep-card">
                <div class="adm-card__header"><div>
                  <h3 class="adm-card__title">Patients</h3>
                  <p class="adm-card__hint">Gender and age of booked visits.</p>
                </div></div>
                <div class="adm-card__body">
                  <div v-if="loading" class="adm-skeleton rep-chart-skel rep-chart-skel--sm"></div>
                  <template v-else-if="hasData">
                    <div class="rep-split" role="img" :aria-label="genderAria">
                      <span v-for="g in genderParts" :key="g.label" :style="{ flexGrow: g.value, background: g.color }"></span>
                    </div>
                    <div class="rep-split-legend">
                      <span v-for="g in genderParts" :key="g.label"><i :style="{ background: g.color }"></i>{{ g.label }} <b>{{ g.value }}</b> ({{ pct(g.value, genderTotal) }})</span>
                    </div>
                    <h4 class="rep-sub">Age groups</h4>
                    <div v-for="a in ageRows" :key="a.label" class="rep-bar-row">
                      <span class="rep-bar-row__label">{{ a.label }}</span>
                      <span class="rep-bar-row__track"><span :style="{ width: barWidth(a.value, ageMax) }"></span></span>
                      <span class="rep-bar-row__value">{{ a.value }}</span>
                    </div>
                  </template>
                  <div v-else class="adm-muted rep-none">No data</div>
                </div>
              </section>
              <section class="adm-card rep-card">
                <div class="adm-card__header"><div>
                  <h3 class="adm-card__title">Top reasons for visit</h3>
                  <p class="adm-card__hint">As entered when booking.</p>
                </div></div>
                <div class="adm-card__body">
                  <div v-if="loading" class="adm-skeleton rep-chart-skel rep-chart-skel--sm"></div>
                  <template v-else-if="report && report.top_reasons.length">
                    <div v-for="r in report.top_reasons" :key="r.reason" class="rep-bar-row">
                      <span class="rep-bar-row__label rep-bar-row__label--wide" :title="r.reason">{{ r.reason }}</span>
                      <span class="rep-bar-row__track"><span :style="{ width: barWidth(r.total, report.top_reasons[0].total) }"></span></span>
                      <span class="rep-bar-row__value">{{ r.total }}</span>
                    </div>
                  </template>
                  <div v-else class="adm-muted rep-none">No reasons recorded</div>
                </div>
              </section>
            </div>

            <!-- By clinic -->
            <section v-if="report && report.clinics.length > 1 && !clinicid" class="adm-card rep-card">
              <div class="adm-card__header"><div>
                <h3 class="adm-card__title">By clinic</h3>
                <p class="adm-card__hint">Where your appointments happen.</p>
              </div></div>
              <div class="adm-table-wrap">
                <table class="adm-table">
                  <thead>
                    <tr><th>Clinic</th><th class="rep-num">Appointments</th><th class="rep-num">Visited</th><th class="rep-num">No-show</th><th class="rep-num">Cancelled</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in report.clinics" :key="c.clinicid">
                      <td class="adm-strong">{{ c.clinicname || c.clinicid }}</td>
                      <td class="rep-num">{{ c.total }}</td>
                      <td class="rep-num">{{ c.visited }}</td>
                      <td class="rep-num">{{ c.no_show }}</td>
                      <td class="rep-num">{{ c.cancelled }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
    <doctorfooter />
  </div>
</template>

<script>
import axios from "axios";
import "../admin/admin.css";
import { errorMessage } from "../admin/admin-utils";

// Categorical slots 1-4 in their validated order (dataviz reference palette).
const OUTCOMES = [
  { key: "visited", label: "Visited", color: "#2a78d6" },
  { key: "no_show", label: "No-show", color: "#eb6834" },
  { key: "booked", label: "Booked", color: "#1baf7a" },
  { key: "cancelled", label: "Cancelled", color: "#eda100" },
];
const SERIES_COLOR = "#2a78d6";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const INK = { primary: "#101828", secondary: "#667085", grid: "#eaecf0" };

function iso(d) {
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function presetRange(id) {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth();
  switch (id) {
    case "last-month": return [new Date(y, m - 1, 1), new Date(y, m, 0)];
    case "3-months": return [new Date(y, m - 2, 1), today];
    case "year": return [new Date(y, 0, 1), today];
    case "this-month":
    default: return [new Date(y, m, 1), today];
  }
}

function baseChart(extra = {}) {
  return {
    chart: { toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: false }, parentHeightOffset: 0 },
    dataLabels: { enabled: false },
    grid: { borderColor: INK.grid, strokeDashArray: 3, xaxis: { lines: { show: false } } },
    legend: { show: false },
    states: { hover: { filter: { type: "darken", value: 0.85 } } },
    stroke: { show: true, width: 2, colors: ["#fff"] }, // 2px surface gap between fills
    tooltip: { theme: "light", shared: true, intersect: false },
    ...extra,
  };
}

/** Counts are whole numbers: never draw 0.7 appointments on the axis. */
function countAxis(max) {
  const top = Math.max(1, max);
  return { min: 0, max: top <= 4 ? top : undefined, tickAmount: Math.min(4, top), forceNiceScale: top > 4,
           labels: { style: { colors: INK.secondary }, formatter: v => Math.round(v) } };
}

export default {
  name: "doctor-reports",
  data() {
    return {
      OUTCOMES,
      presets: [
        { id: "this-month", label: "This month" },
        { id: "last-month", label: "Last month" },
        { id: "3-months", label: "Last 3 months" },
        { id: "year", label: "This year" },
        { id: "custom", label: "Custom" },
      ],
      preset: "this-month",
      customStart: "",
      customEnd: "",
      clinicid: "",
      clinics: [],
      report: null,
      loading: true,
      error: "",
      showTable: false,
      exporting: false,
      requestId: 0,
    };
  },
  computed: {
    doctorId() {
      return sessionStorage.getItem("userdbid");
    },
    range() {
      if (this.preset === "custom" && this.customStart && this.customEnd) return [this.customStart, this.customEnd];
      const [s, e] = presetRange(this.preset);
      return [iso(s), iso(e)];
    },
    rangeLabel() {
      return `${this.dateLabel(this.range[0])} – ${this.dateLabel(this.range[1])}`;
    },
    prevLabel() {
      const p = this.report.previous_range;
      return `${this.dateLabel(p.start)} – ${this.dateLabel(p.end)}`;
    },
    hasData() {
      return !!this.report && this.report.summary.total > 0;
    },
    kpis() {
      const s = this.report ? this.report.summary : {};
      const p = this.report ? this.report.previous_summary : {};
      const n = v => (v === undefined || v === null ? "—" : v.toLocaleString("en-IN"));
      return [
        { label: "Appointments", value: n(s.total), delta: this.delta(s.total, p.total) },
        { label: "Visited", value: n(s.visited), delta: this.delta(s.visited, p.visited) },
        { label: "Show-up rate", value: s.show_rate === null || s.show_rate === undefined ? "—" : `${s.show_rate}%`,
          delta: this.delta(s.show_rate, p.show_rate, { points: true }), note: "visited ÷ (visited + no-show)" },
        { label: "Cancelled", value: n(s.cancelled), delta: this.delta(s.cancelled, p.cancelled, { lowerIsBetter: true }),
          note: s.cancel_rate !== null && s.cancel_rate !== undefined ? `${s.cancel_rate}% of bookings` : "" },
        { label: "Patients", value: n(s.patients), delta: this.delta(s.patients, p.patients) },
        { label: "New patients", value: n(s.new_patients), delta: this.delta(s.new_patients, p.new_patients),
          note: "first visit with you" },
      ];
    },
    nonEmptySeries() {
      return this.report ? this.report.series.filter(r => r.total > 0) : [];
    },
    trendSeries() {
      return OUTCOMES.map(o => ({ name: o.label, data: this.report.series.map(r => r[o.key]) }));
    },
    trendOptions() {
      const monthly = this.report.bucket === "month";
      return baseChart({
        chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: false } },
        colors: OUTCOMES.map(o => o.color),
        plotOptions: { bar: { columnWidth: monthly ? "45%" : "70%", borderRadius: 4, borderRadiusApplication: "end",
                              borderRadiusWhenStacked: "last" } },
        xaxis: {
          categories: this.report.series.map(r => this.periodLabel(r.period, true)),
          labels: { style: { colors: INK.secondary }, rotate: 0, hideOverlappingLabels: true },
          tickAmount: monthly ? undefined : Math.min(10, this.report.series.length),
          axisBorder: { color: INK.grid }, axisTicks: { show: false },
        },
        tooltip: { shared: true, intersect: false, y: { formatter: v => `${v}` } },
        yaxis: countAxis(Math.max(0, ...this.report.series.map(r => r.total))),
      });
    },
    weekdaySeries() {
      const byDay = Object.fromEntries((this.report.weekdays || []).map(r => [r.weekday, r.total]));
      return [{ name: "Appointments", data: WEEKDAYS.map((_, i) => byDay[i + 1] || 0) }];
    },
    weekdayOptions() {
      return baseChart({
        colors: [SERIES_COLOR],
        plotOptions: { bar: { columnWidth: "50%", borderRadius: 4, borderRadiusApplication: "end" } },
        xaxis: { categories: WEEKDAYS, labels: { style: { colors: INK.secondary } }, axisTicks: { show: false }, axisBorder: { color: INK.grid } },
        yaxis: countAxis(Math.max(0, ...this.weekdaySeries[0].data)),
        tooltip: { shared: false, intersect: true },
      });
    },
    hourSeries() {
      return [{ name: "Appointments", data: this.report.hours.map(h => h.total) }];
    },
    hourOptions() {
      return baseChart({
        colors: [SERIES_COLOR],
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 4, borderRadiusApplication: "end" } },
        xaxis: { categories: this.report.hours.map(h => this.hourLabel(h.hour)),
                 labels: { style: { colors: INK.secondary }, rotate: 0, hideOverlappingLabels: true },
                 axisTicks: { show: false }, axisBorder: { color: INK.grid } },
        yaxis: countAxis(Math.max(0, ...this.report.hours.map(h => h.total))),
        tooltip: { shared: false, intersect: true },
      });
    },
    genderParts() {
      const g = this.report.gender;
      return [
        { label: "Male", value: g.male, color: OUTCOMES[0].color },
        { label: "Female", value: g.female, color: OUTCOMES[1].color },
        { label: "Other", value: g.other, color: OUTCOMES[2].color },
      ].filter(x => x.value > 0);
    },
    genderTotal() {
      return this.genderParts.reduce((s, g) => s + g.value, 0);
    },
    genderAria() {
      return this.genderParts.map(g => `${g.label} ${g.value}`).join(", ");
    },
    ageRows() {
      const a = this.report.age_groups;
      const rows = ["0-12", "13-17", "18-35", "36-60", "60+"].map(k => ({ label: k === "60+" ? "Over 60" : `${k} yrs`, value: a[k] }));
      if (a.unknown) rows.push({ label: "Not given", value: a.unknown });
      return rows;
    },
    ageMax() {
      return Math.max(1, ...this.ageRows.map(r => r.value));
    },
  },
  methods: {
    dateLabel(value) {
      return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    },
    periodLabel(value, short = false) {
      const d = new Date(`${value}T00:00:00`);
      if (this.report && this.report.bucket === "month") return d.toLocaleDateString("en-IN", { month: "short", year: short ? "2-digit" : "numeric" });
      return d.toLocaleDateString("en-IN", short ? { day: "numeric", month: "short" } : { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    },
    hourLabel(h) {
      const suffix = h < 12 ? "am" : "pm";
      return `${h % 12 === 0 ? 12 : h % 12}${suffix}`;
    },
    pct(value, total) {
      return total ? `${Math.round((100 * value) / total)}%` : "0%";
    },
    barWidth(value, max) {
      return `${Math.max(value ? 3 : 0, Math.round((100 * value) / (max || 1)))}%`;
    },
    delta(now, before, { lowerIsBetter = false, points = false } = {}) {
      if (now === null || now === undefined || before === null || before === undefined) return null;
      if (!points && before === 0) return null;
      const change = points ? now - before : Math.round((100 * (now - before)) / before);
      if (change === 0) return { up: true, text: "no change", tone: "rep-flat" };
      const good = lowerIsBetter ? change < 0 : change > 0;
      return { up: change > 0, text: `${Math.abs(change)}${points ? " pts" : "%"} vs before`, tone: good ? "rep-good" : "rep-bad" };
    },
    choosePreset(id) {
      this.preset = id;
      if (id === "custom") {
        if (!this.customStart) [this.customStart, this.customEnd] = this.range;
        return;
      }
      this.load();
    },
    async loadClinics() {
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/clinicids`, { params: { details: true } });
        this.clinics = data || [];
      } catch (error) {
        this.clinics = [];
      }
    },
    async load() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = "";
      try {
        const [start, end] = this.range;
        const { data } = await axios.get(`/doctors/${this.doctorId}/report`, {
          params: { start, end, clinicid: this.clinicid || undefined },
        });
        if (requestId === this.requestId) this.report = data;
      } catch (error) {
        if (requestId === this.requestId) {
          this.report = null;
          this.error = errorMessage(error, "Could not load the report.");
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    exportRows() {
      const s = this.report.summary;
      const summary = [
        ["Period", this.rangeLabel],
        ["Clinic", this.clinicid ? (this.clinics.find(c => c.clinicid === this.clinicid) || {}).clinicname || this.clinicid : "All clinics"],
        ["Appointments", s.total], ["Visited", s.visited], ["No-show", s.no_show], ["Cancelled", s.cancelled],
        ["Booked (upcoming)", s.upcoming], ["Booked but not marked", s.unmarked],
        ["Show-up rate", s.show_rate === null ? "—" : `${s.show_rate}%`], ["Patients", s.patients], ["New patients", s.new_patients],
      ];
      const trendHead = [this.report.bucket === "month" ? "Month" : "Date", ...OUTCOMES.map(o => o.label), "Total"];
      const trend = this.nonEmptySeries.map(r => [this.periodLabel(r.period), ...OUTCOMES.map(o => r[o.key]), r.total]);
      const clinicHead = ["Clinic", "Appointments", "Visited", "No-show", "Cancelled"];
      const clinics = this.report.clinics.map(c => [c.clinicname || c.clinicid, c.total, c.visited, c.no_show, c.cancelled]);
      return { summary, trendHead, trend, clinicHead, clinics };
    },
    fileName(ext) {
      return `yourdoc-report-${this.range[0]}-to-${this.range[1]}.${ext}`;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const { default: ExcelJS } = await import("exceljs");
        const rows = this.exportRows();
        const book = new ExcelJS.Workbook();
        const add = (name, head, body) => {
          const sheet = book.addWorksheet(name);
          if (head) sheet.addRow(head).font = { bold: true };
          body.forEach(r => sheet.addRow(r));
          sheet.columns.forEach(col => { col.width = 22; });
        };
        add("Summary", null, rows.summary);
        add("Appointments", rows.trendHead, rows.trend);
        add("Clinics", rows.clinicHead, rows.clinics);
        const blob = new Blob([await book.xlsx.writeBuffer()], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        this.download(blob, this.fileName("xlsx"));
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      this.exporting = true;
      try {
        const { default: jsPDF } = await import("jspdf");
        await import("jspdf-autotable");
        const rows = this.exportRows();
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text("Appointments report", 14, 18);
        doc.setFontSize(10);
        doc.text(this.rangeLabel, 14, 25);
        const style = { headStyles: { fillColor: [42, 120, 214] }, styles: { fontSize: 9 } };
        doc.autoTable({ startY: 30, body: rows.summary, theme: "plain", ...style });
        doc.autoTable({ startY: doc.lastAutoTable.finalY + 8, head: [rows.trendHead], body: rows.trend, ...style });
        if (rows.clinics.length) doc.autoTable({ startY: doc.lastAutoTable.finalY + 8, head: [rows.clinicHead], body: rows.clinics, ...style });
        doc.save(this.fileName("pdf"));
      } finally {
        this.exporting = false;
      }
    },
    download(blob, name) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    },
  },
  created() {
    this.loadClinics();
    this.load();
  },
};
</script>

<style scoped>
.rep {
  font-size: 14px;
}

.rep-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.rep-custom {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rep-custom .adm-input {
  width: 150px;
  padding: 6px 10px;
}

.rep-clinic {
  width: auto;
  min-width: 170px;
  padding: 7px 10px;
}

.rep-export {
  display: flex;
  gap: 6px;
}

.rep-range {
  font-size: 13px;
  margin: 10px 0 16px;
}

.rep-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.rep-kpi {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rep-kpi__label {
  color: var(--adm-muted);
  font-size: 13px;
  font-weight: 600;
}

.rep-kpi__value {
  color: var(--adm-ink);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.25;
}

.rep-kpi__delta {
  font-size: 12px;
  font-weight: 600;
}

.rep-good {
  color: var(--adm-success-700);
}

.rep-bad {
  color: var(--adm-danger-700);
}

.rep-flat {
  color: var(--adm-muted);
  font-weight: 500;
}

.rep-skel {
  display: block;
  width: 60px;
  height: 28px;
  margin: 2px 0;
}

.rep-card {
  margin-bottom: 16px;
}

.rep-card .adm-card__body {
  padding-top: 12px;
}

.rep-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}

.rep-grid .rep-card {
  margin-bottom: 16px;
}

.rep-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 13px;
  color: var(--adm-ink-2);
  margin-bottom: 6px;
}

.rep-legend i,
.rep-split-legend i {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: -1px;
}

.rep-chart-skel {
  height: 280px;
  width: 100%;
}

.rep-chart-skel--sm {
  height: 200px;
}

.rep-table {
  max-height: 320px;
  overflow-y: auto;
}

.rep-num {
  text-align: right !important;
  font-variant-numeric: tabular-nums;
}

.rep-none {
  padding: 40px 0;
  text-align: center;
}

.rep-split {
  display: flex;
  gap: 2px;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
}

.rep-split span {
  flex-basis: 0;
}

.rep-split-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 13px;
  margin: 10px 0 4px;
  color: var(--adm-ink-2);
}

.rep-split-legend b {
  color: var(--adm-ink);
}

.rep-sub {
  font-size: 13px;
  font-weight: 700;
  color: var(--adm-ink);
  margin: 16px 0 8px;
}

.rep-bar-row {
  display: grid;
  grid-template-columns: 80px 1fr 40px;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 13px;
}

.rep-bar-row__label {
  color: var(--adm-ink-2);
}

.rep-bar-row:has(.rep-bar-row__label--wide) {
  grid-template-columns: minmax(0, 150px) 1fr 40px;
}

.rep-bar-row__label--wide {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rep-bar-row__track {
  height: 10px;
  background: var(--adm-line-2);
  border-radius: 5px;
  overflow: hidden;
}

.rep-bar-row__track span {
  display: block;
  height: 100%;
  background: #2a78d6;
  border-radius: 0 4px 4px 0;
}

.rep-bar-row__value {
  text-align: right;
  color: var(--adm-ink);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.rep-link {
  font-weight: 700;
  color: inherit;
  text-decoration: underline;
}
</style>
